import { Auth, Marketplace } from "../constants";

// The grant catalogues: every screen-facing permission flag, named once. Each
// grant is self-contained: the claims that satisfy it and the resource type the
// check applies to. WithAuth resolves the catalogues and screens read the
// booleans from props.permissions; handlers pass a single member to
// canUserPerformAny({ grant: AppSpaceGrantCatalogue.canEditSettings }). The active
// tenant/org/appspace ids are supplied automatically; product screens declare
// their productId. Add new grants here, not inline in screens.

// One catalogue entry: the flag passes when the user holds ANY of these claims
// against the target resource type.
const defineGrant = (targetResourceType, anyOfTheseClaims) => ({
    targetResourceType,
    claims: anyOfTheseClaims,
});

const tenant = Auth.Scopes.manageTenant.claims;
const org = Auth.Scopes.manageOrg.claims;
const appSpace = Auth.Scopes.manageAppSpace.claims;
const app = Auth.Scopes.manageApp.claims;

export const TenantGrantCatalogue = {
    canReadSettings: defineGrant(Marketplace.ResourceTypes.Tenant, [tenant.readTenant, tenant.cruTenant, tenant.crudTenant]),
    canEditSettings: defineGrant(Marketplace.ResourceTypes.Tenant, [tenant.cruTenant, tenant.crudTenant]),
    canReadOrgs: defineGrant(Marketplace.ResourceTypes.Tenant, [tenant.readTenantOrgs, tenant.cruTenantOrgs, tenant.crudTenantOrgs]),
    canReadUsers: defineGrant(Marketplace.ResourceTypes.Tenant, [tenant.readTenantUsers, tenant.cruTenantUsers, tenant.crudTenantUsers]),
    canEditUsers: defineGrant(Marketplace.ResourceTypes.Tenant, [tenant.cruTenantUsers, tenant.crudTenantUsers]),
    // Sending an invitation, which can make someone an owner, and deleting a pending one: Identity
    // asks for full rights over the tenant's users on both. Seeing the pending invitations is
    // canReadUsers.
    canManageInvitations: defineGrant(Marketplace.ResourceTypes.Tenant, [tenant.crudTenantUsers]),
    canAddOrgs: defineGrant(Marketplace.ResourceTypes.Tenant, [tenant.cruTenantOrgs, tenant.crudTenantOrgs]),
    canDeleteOrgs: defineGrant(Marketplace.ResourceTypes.Tenant, [tenant.crudTenant]),
    canChooseGitHubOrganisationName: defineGrant(Marketplace.ResourceTypes.Tenant, [tenant.crudTenant]),
};

export const OrganisationGrantCatalogue = {
    canReadSettings: defineGrant(Marketplace.ResourceTypes.Organisation, [org.readOrganisation, org.cruOrganisation, org.crudOrganisation]),
    canEditSettings: defineGrant(Marketplace.ResourceTypes.Organisation, [org.cruOrganisation, org.crudOrganisation]),
    canReadUsers: defineGrant(Marketplace.ResourceTypes.Organisation, [org.readOrganisationUsers, org.cruOrganisationUsers, org.crudOrganisationUsers]),
    canEditUsers: defineGrant(Marketplace.ResourceTypes.Organisation, [org.cruOrganisationUsers, org.crudOrganisationUsers]),
    canDeleteUsers: defineGrant(Marketplace.ResourceTypes.Organisation, [org.crudOrganisationUsers]),
    // An organisation's teams are for the people who manage its users and for those who manage
    // the tenant's users, the claims Marketplace asks for on its team addresses. A claim held
    // on the tenant counts for every organisation in it.
    canReadTeams: defineGrant(Marketplace.ResourceTypes.Organisation, [org.readOrganisationUsers, org.cruOrganisationUsers, org.crudOrganisationUsers, tenant.readTenantUsers, tenant.cruTenantUsers, tenant.crudTenantUsers]),
    canEditTeams: defineGrant(Marketplace.ResourceTypes.Organisation, [org.cruOrganisationUsers, org.crudOrganisationUsers, tenant.cruTenantUsers, tenant.crudTenantUsers]),
    canDeleteTeams: defineGrant(Marketplace.ResourceTypes.Organisation, [org.crudOrganisationUsers, tenant.crudTenantUsers]),
    canReadAppSpaces: defineGrant(Marketplace.ResourceTypes.Organisation, [org.readOrganisationAppSpaces, org.cruOrganisationAppSpaces, org.crudOrganisationAppSpaces]),
    canAddAppSpaces: defineGrant(Marketplace.ResourceTypes.Organisation, [org.cruOrganisationAppSpaces, org.crudOrganisationAppSpaces]),
};

export const AppSpaceGrantCatalogue = {
    canReadSettings: defineGrant(Marketplace.ResourceTypes.AppSpace, [appSpace.readAppSpace, appSpace.cruAppSpace, appSpace.crudAppSpace]),
    canEditSettings: defineGrant(Marketplace.ResourceTypes.AppSpace, [appSpace.cruAppSpace, appSpace.crudAppSpace]),
    canReadUsers: defineGrant(Marketplace.ResourceTypes.AppSpace, [appSpace.readAppSpaceUsers, appSpace.cruAppSpaceUsers, appSpace.crudAppSpaceUsers]),
    canEditUsers: defineGrant(Marketplace.ResourceTypes.AppSpace, [appSpace.cruAppSpaceUsers, appSpace.crudAppSpaceUsers]),
    canDeleteAppSpace: defineGrant(Marketplace.ResourceTypes.AppSpace, [appSpace.crudAppSpace]),
    // Raising the limits of a job that waits at its agent budget: full control of the job's AppSpace
    // or of its organisation, the claims JobRunner's CanChangeJobAgentBudget asks for (the owner's
    // answers of 30 Oct and 4 Nov 2026). Held on the organisation or the tenant, they count too.
    canChangeJobAgentBudget: defineGrant(Marketplace.ResourceTypes.AppSpace, [appSpace.crudAppSpace, org.crudOrganisation]),
};

export const AppGrantCatalogue = {
    // Reading the app's health and compliance figures: any right on the app itself, the claims
    // the app's page asks for before it shows its Traffic, Vulnerabilities, Dependencies,
    // DevSecOps and Standards tabs.
    canReadApp: defineGrant(Marketplace.ResourceTypes.App, [app.ReadApp, app.CruApp, app.CrudApp]),
    canDeleteApp: defineGrant(Marketplace.ResourceTypes.App, [app.CrudApp]),
    // Accepting one of the app's open critical findings, so it no longer holds the app's releases:
    // full control of the app, the claim Asset Health's CanAcceptFindings asks for (the owner's
    // design of 22 Oct 2026). Held on the app's AppSpace, organisation or tenant, it counts too.
    canAcceptFindings: defineGrant(Marketplace.ResourceTypes.App, [app.CrudApp]),
    canEditAppUsers: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppUsers, app.CrudAppUsers]),
    canDeleteAppUsers: defineGrant(Marketplace.ResourceTypes.App, [app.CrudAppUsers]),
    // Delete is strict crud-only; Revoke is an update (cru or crud).
    canDeletePat: defineGrant(Marketplace.ResourceTypes.App, [app.CrudAppPat]),
    canRevokePat: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppPat, app.CrudAppPat]),
    canCreatePat: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppPat, app.CrudAppPat]),
    canAllocateProdDb: defineGrant(Marketplace.ResourceTypes.App, [app.CrudAppProdEnvironments]),
    canEditProdSecrets: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppProdSecrets, app.CrudAppProdSecrets]),
    canEditNonProdSecrets: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppProdNonSecrets, app.CrudAppNonProdSecrets]),
    canDeleteProdSecrets: defineGrant(Marketplace.ResourceTypes.App, [app.CrudAppProdSecrets]),
    canDeleteNonProdSecrets: defineGrant(Marketplace.ResourceTypes.App, [app.CrudAppNonProdSecrets]),
    canEditApp: defineGrant(Marketplace.ResourceTypes.App, [app.CruApp, app.CrudApp]),
    canManageMcp: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppMcp, app.CrudAppMcp]),
    canApproveNonProdRelease: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppNonProdEnvironments, app.CrudAppNonProdEnvironments]),
    canApproveProdRelease: defineGrant(Marketplace.ResourceTypes.App, [app.CrudAppProdEnvironments]),
    // Reading a container's live logs is a read on that environment tier.
    canReadProdContainerLogs: defineGrant(Marketplace.ResourceTypes.App, [app.ReadAppProdEnvironments, app.CruAppProdEnvironments, app.CrudAppProdEnvironments]),
    canReadNonProdContainerLogs: defineGrant(Marketplace.ResourceTypes.App, [app.ReadAppNonProdEnvironments, app.CruAppNonProdEnvironments, app.CrudAppNonProdEnvironments]),
    // Restarting a pod changes that environment tier's running workload: an update (cru or crud).
    canRestartProdPod: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppProdEnvironments, app.CrudAppProdEnvironments]),
    canRestartNonProdPod: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppNonProdEnvironments, app.CrudAppNonProdEnvironments]),
    // Cancelling a build stops a run, so it is for the people who may start one (cru or crud).
    canCancelBuild: defineGrant(Marketplace.ResourceTypes.App, [app.CruApp, app.CrudApp]),
    // Cancelling a deployment changes what reaches that environment tier: an update (cru or crud).
    canCancelProdDeployment: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppProdEnvironments, app.CrudAppProdEnvironments]),
    canCancelNonProdDeployment: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppNonProdEnvironments, app.CrudAppNonProdEnvironments]),
    // Reading a deployment's steps and their logs is a read on that environment tier, as a container's logs are.
    canReadProdDeploymentLogs: defineGrant(Marketplace.ResourceTypes.App, [app.ReadAppProdEnvironments, app.CruAppProdEnvironments, app.CrudAppProdEnvironments]),
    canReadNonProdDeploymentLogs: defineGrant(Marketplace.ResourceTypes.App, [app.ReadAppNonProdEnvironments, app.CruAppNonProdEnvironments, app.CrudAppNonProdEnvironments]),
    // An app's toggles (Feature Management). The levels are the service's own: seeing the toggles,
    // changing a toggle's definition, changing what a toggle does in an environment tier, and the
    // full permission, which keys, archiving and deleting need.
    canViewToggles: defineGrant(Marketplace.ResourceTypes.App, [app.ReadAppToggles, app.CruAppNonProdToggles, app.CruAppProdToggles, app.CrudAppToggles]),
    canAuthorToggles: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppNonProdToggles, app.CrudAppToggles]),
    canChangeNonProdToggles: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppNonProdToggles, app.CrudAppToggles]),
    canChangeProdToggles: defineGrant(Marketplace.ResourceTypes.App, [app.CruAppProdToggles, app.CrudAppToggles]),
    canAdministerToggles: defineGrant(Marketplace.ResourceTypes.App, [app.CrudAppToggles]),
};

// Every claim named by any grant in a catalogue, without duplicates: the
// candidate list for one bulk granted-claims call.
export const getAllClaimsFromGrantCatalogue = (grantCatalogue) => {
    const claims = Object.values(grantCatalogue).flatMap((grant) => grant.claims);
    return [...new Set(claims)];
};

// The resource type a catalogue's claims are checked against. Every grant in a
// catalogue targets the same type, so the first member answers for all.
export const getResourceTypeFromGrantCatalogue = (grantCatalogue) =>
    Object.values(grantCatalogue)[0]?.targetResourceType;

// A catalogue plus the user's granted claims, turned into flat verdicts:
// { canReadSettings: true, canEditSettings: false, ... }. A grant passes when
// any one of its claims was granted.
export const buildPermissionsFromGrantCatalogue = (grantCatalogue, grantedClaims) => {
    const flags = {};

    for (const [name, grant] of Object.entries(grantCatalogue)) {
        flags[name] = grant.claims.some((claim) => grantedClaims.includes(claim));
    }

    return flags;
};
